import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import FlashcardFront from './FlashcardFront';
import FlashcardBack from './FlashcardBack';
import { playFlipSound } from '../utils/soundEffects';

/**
 * Flashcard — Inkwell editorial 3D flip card.
 * Both faces render simultaneously inside a perspective container; flipping
 * rotates the inner wrapper around Y. Each face owns its own surface
 * (border + radius) so the card looks complete on either side.
 */
function Flashcard({ word }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [innerHeight, setInnerHeight] = useState(0);

  // Reset flip on word change
  useEffect(() => {
    setIsFlipped(false);
  }, [word?.id]);

  const handleFlip = useCallback(() => {
    playFlipSound();
    setIsFlipped((prev) => !prev);
  }, []);

  // Keyboard: space / enter flips
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleFlip();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleFlip]);

  // Match container height to whichever face is taller so the back-face
  // (absolute-positioned) doesn't get clipped. The back face wrapper has
  // `position: absolute; inset: 0`, which makes its own offsetHeight equal
  // the parent's height — circular. Measure its first in-flow child instead,
  // which is the FlashcardBack outer div with its natural content height.
  useLayoutEffect(() => {
    const measure = () => {
      const fh = frontRef.current?.firstElementChild?.offsetHeight ?? frontRef.current?.offsetHeight ?? 0;
      const bh = backRef.current?.firstElementChild?.offsetHeight ?? 0;
      setInnerHeight(Math.max(fh, bh));
    };
    measure();
    const r = new ResizeObserver(measure);
    if (frontRef.current?.firstElementChild) r.observe(frontRef.current.firstElementChild);
    if (backRef.current?.firstElementChild) r.observe(backRef.current.firstElementChild);
    return () => r.disconnect();
  }, [word?.id]);

  if (!word) {
    return (
      <div style={{ padding: 64, textAlign: 'center', color: 'var(--ink-3)' }}>
        No word to display
      </div>
    );
  }

  return (
    <div className="ink-flashcard">
      <div
        className={`ink-flashcard-inner ${isFlipped ? 'is-flipped' : ''}`}
        onClick={handleFlip}
        role="button"
        tabIndex={0}
        aria-label={isFlipped ? 'Card back, click to flip to front' : 'Card front, click to flip to back'}
        style={{ height: innerHeight ? `${innerHeight}px` : 'auto' }}
      >
        <div className="ink-flashcard-face" ref={frontRef}>
          <FlashcardFront word={word} />
        </div>
        <div className="ink-flashcard-face ink-flashcard-face--back" ref={backRef}>
          <FlashcardBack word={word} />
        </div>
      </div>
    </div>
  );
}

export default Flashcard;
